import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import Loader from "shared/components/loader/animation";
import { toastMessage } from "shared/components/toast";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import useDebounce from "shared/customHooks/useDebounce";
import { routeConstant } from "shared/routes/routesConstant";
import PaginationComponent from "shared/components/pagination";
import Confirmation from "shared/modals/confirmation";
import {
  getQRCodesListService,
  getEventNamesService,
  activateQRCodeService,
  deactivateQRCodeService,
  deleteQRCodeService,
  downloadSingleQRCodeService,
  downloadEventQRCodesService,
  downloadAllQRCodesService,
} from "shared/services/qrCodeService";
import GenerateQRModal from "./modals/GenerateQRModal";
import GenerateBulkQRModal from "./modals/GenerateBulkQRModal";
import styles from "./style.module.scss";

interface QRCode {
  id: number;
  event_name: string | null;
  code: string;
  plan_type: number;
  plan_type_label: string;
  duration: number;
  coins: number;
  price: number;
  status: boolean;
  status_label: string;
  is_used: boolean;
  used_by: any;
  used_at: string | null;
  expires_at: string;
  is_expired: boolean;
  created_by: {
    id: number;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

function QRManagement() {
  const [loading, setLoading] = useState<boolean>(false);
  const [qrCodes, setQRCodes] = useState<QRCode[]>([]);
  const [eventNames, setEventNames] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(15);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedQR, setSelectedQR] = useState<QRCode | null>(null);
  const [openGenerateModal, setOpenGenerateModal] = useState<boolean>(false);
  const [openBulkModal, setOpenBulkModal] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<string>("");

  // Filters
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [selectedEvent, setSelectedEvent] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("active");
  const [selectedUsed, setSelectedUsed] = useState<string>("0");
  const [selectedPlanType, setSelectedPlanType] = useState<string>("");

  useBreadcrumbs({
    title: "QR Management",
    path: routeConstant.qrCodeManagement.path,
  });

  useDebounce(
    () => {
      setSearchText(searchTerm.trim());
    },
    [searchTerm],
    800
  );

  useEffect(() => {
    fetchEventNames();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchQRCodes();
    // eslint-disable-next-line
  }, [currentPage, perPage]);

  // Filter/search changes always restart from page 1, so the request never
  // carries a stale page number. Skipped on mount — the effect above fetches.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentPage === 1) {
      fetchQRCodes();
    } else {
      setCurrentPage(1);
    }
    // eslint-disable-next-line
  }, [searchText, selectedEvent, selectedStatus, selectedUsed, selectedPlanType]);

  const fetchQRCodes = () => {
    setLoading(true);
    const params: any = {
      page: currentPage,
      per_page: perPage,
    };

    if (searchText) params.search = searchText;
    if (selectedEvent) {
      params.event_name = selectedEvent === "null" ? "null" : selectedEvent;
    }
    if (selectedStatus !== "") params.status = selectedStatus;
    if (selectedUsed !== "") params.is_used = selectedUsed;
    if (selectedPlanType) params.plan_type = selectedPlanType;

    getQRCodesListService(params)
      .then(({ data }) => {
        if (data?.success || data?.status) {
          setQRCodes(data?.data?.data || []);
          setTotalPages(data?.data?.last_page || 1);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log("err", err?.response?.data?.message);
        toastMessage("error", err?.response?.data?.message || "Failed to fetch QR codes");
        setLoading(false);
      });
  };

  const fetchEventNames = () => {
    getEventNamesService()
      .then(({ data }) => {
        if (data?.success || data?.status) {
          setEventNames(data?.data || []);
        }
      })
      .catch((err) => {
        console.log("err", err?.response?.data?.message);
      });
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, qr: QRCode) => {
    setAnchorEl(event.currentTarget);
    setSelectedQR(qr);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleActivate = () => {
    if (selectedQR) {
      setLoading(true);
      activateQRCodeService(selectedQR.id)
        .then(({ data }) => {
          if (data?.success || data?.status) {
            toastMessage("success", data?.message || "QR code activated successfully");
            fetchQRCodes();
          }
          setLoading(false);
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.message || "Failed to activate QR code");
          setLoading(false);
        });
    }
    handleMenuClose();
  };

  const handleDeactivate = () => {
    if (selectedQR) {
      setLoading(true);
      deactivateQRCodeService(selectedQR.id)
        .then(({ data }) => {
          if (data?.success || data?.status) {
            toastMessage("success", data?.message || "QR code deactivated successfully");
            fetchQRCodes();
          }
          setLoading(false);
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.message || "Failed to deactivate QR code");
          setLoading(false);
        });
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    setConfirmAction("delete");
    setOpenConfirm(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedQR) {
      setLoading(true);
      deleteQRCodeService(selectedQR.id)
        .then(({ data }) => {
          if (data?.success || data?.status) {
            toastMessage("success", data?.message || "QR code deleted successfully");
            fetchQRCodes();
          }
          setLoading(false);
          setOpenConfirm(false);
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.message || "Failed to delete QR code");
          setLoading(false);
          setOpenConfirm(false);
        });
    }
  };

  const handleDownloadSingle = () => {
    if (selectedQR) {
      setLoading(true);
      downloadSingleQRCodeService(selectedQR.id)
        .then(({ data }) => {
          if ((data?.success || data?.status) && data?.data?.image) {
            const link = document.createElement("a");
            link.href = `data:image/png;base64,${data.data.image}`;
            link.download = data.data.filename || `qr_code_${selectedQR.id}.png`;
            link.click();
            toastMessage("success", "QR code downloaded successfully");
          }
          setLoading(false);
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.message || "Failed to download QR code");
          setLoading(false);
        });
    }
    handleMenuClose();
  };

  const handleDownloadEvent = (eventName: string) => {
    setLoading(true);
    downloadEventQRCodesService(eventName)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `event_${eventName.replace(/\s+/g, "_")}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toastMessage("success", "Event QR codes downloaded successfully");
        setLoading(false);
      })
      .catch((err) => {
        toastMessage("error", "Failed to download event QR codes");
        setLoading(false);
      });
  };

  const handleDownloadAll = () => {
    setLoading(true);

    const params: any = {};
    if (searchText) params.search = searchText;
    if (selectedEvent) {
      params.event_name = selectedEvent === "null" ? "null" : selectedEvent;
    }
    if (selectedStatus !== "") params.status = selectedStatus;
    if (selectedUsed !== "") params.is_used = selectedUsed;
    if (selectedPlanType) params.plan_type = selectedPlanType;

    const onProgress = (progressEvent: any) => {
      if (progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Download progress: ${percentCompleted}%`);
      }
    };

    downloadAllQRCodesService(params, onProgress)
      .then((response) => {
        // Check if response has data
        if (!response.data || response.data.size === 0) {
          toastMessage("error", "No QR codes found to download");
          setLoading(false);
          return;
        }

        // Create blob with explicit type
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const timestamp = new Date().getTime();
        link.setAttribute("download", `all_qr_codes_${timestamp}.zip`);
        document.body.appendChild(link);
        link.click();

        // Cleanup
        setTimeout(() => {
          link.remove();
          window.URL.revokeObjectURL(url);
        }, 100);

        toastMessage("success", "All QR codes downloaded successfully");
        setLoading(false);
      })
      .catch((err) => {
        console.error("Download error:", err);
        let errorMessage = "Failed to download all QR codes";

        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          errorMessage = "Download timeout - too many QR codes. Try filtering to reduce the number.";
        } else if (err.message?.includes('Network Error') || err.message?.includes('ERR_CONNECTION_CLOSED')) {
          errorMessage = "Connection lost during download. The file may be too large. Try downloading by event instead.";
        } else if (err.response?.data) {
          // Try to read error message from blob
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result as string);
              toastMessage("error", errorData.message || errorMessage);
            } catch {
              toastMessage("error", errorMessage);
            }
          };
          reader.readAsText(err.response.data);
          setLoading(false);
          return;
        }

        toastMessage("error", errorMessage);
        setLoading(false);
      });
  };

  const clearFilters = () => {
    setSearchTerm("");
    // Cleared here too, so clearing refetches once now instead of again
    // when the debounce settles.
    setSearchText("");
    setSelectedEvent("");
    setSelectedStatus("");
    setSelectedUsed("");
    setSelectedPlanType("");
  };

  const getStatusChip = (qr: QRCode) => {
    if (qr.is_expired) {
      return <Chip label="Expired" size="small" color="error" />;
    }
    if (qr.is_used) {
      return <Chip label="Used" size="small" color="success" />;
    }
    if (qr.status) {
      return <Chip label="Active" size="small" color="primary" />;
    }
    return <Chip label="Inactive" size="small" color="default" />;
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h5" className={styles.title}>
          QR Code Management
        </Typography>
        <Box className={styles.actions}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenGenerateModal(true)}
            className={styles.generateBtn}
          >
            Generate Single
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenBulkModal(true)}
            className={styles.bulkBtn}
          >
            Generate Bulk
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadAll}
          >
            Download All
          </Button>
        </Box>
      </Box>

      <Paper className={styles.filtersContainer}>
        <Box className={styles.filters}>
          <TextField
            placeholder="Search by QR code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: <SearchIcon className={styles.searchIcon} />,
            }}
            className={styles.searchField}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Event Name</InputLabel>
            <Select
              value={selectedEvent}
              label="Event Name"
              onChange={(e) => setSelectedEvent(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="null">No Event</MenuItem>
              {eventNames.map((event) => (
                <MenuItem key={event} value={event}>
                  {event}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Used Status</InputLabel>
            <Select
              value={selectedUsed}
              label="Used Status"
              onChange={(e) => setSelectedUsed(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">Used</MenuItem>
              <MenuItem value="0">Unused</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Plan Type</InputLabel>
            <Select
              value={selectedPlanType}
              label="Plan Type"
              onChange={(e) => setSelectedPlanType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">Individual</MenuItem>
              <MenuItem value="2">Family</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="text"
            startIcon={<FilterListIcon />}
            onClick={clearFilters}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Loader />
      ) : (
        <>
          <TableContainer component={Paper} className={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>QR Code</TableCell>
                  <TableCell>Event Name</TableCell>
                  <TableCell>Plan Type</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Coins</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expires At</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {qrCodes.length > 0 ? (
                  qrCodes.map((qr) => (
                    <TableRow key={qr.id}>
                      <TableCell>
                        <Tooltip title={qr.code}>
                          <Typography variant="body2" className={styles.codeText}>
                            {qr.code.substring(0, 12)}...
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{qr.event_name || "-"}</TableCell>
                      <TableCell>{qr.plan_type_label}</TableCell>
                      <TableCell>{qr.duration} days</TableCell>
                      <TableCell>{qr.coins}</TableCell>
                      <TableCell>{qr.price}</TableCell>
                      <TableCell>{getStatusChip(qr)}</TableCell>
                      <TableCell>
                        {qr.expires_at ? new Date(qr.expires_at).toLocaleDateString() : "-"}
                      </TableCell>
                      <TableCell>{qr.created_by?.name || "-"}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleMenuClick(e, qr)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No QR codes found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className={styles.paginationContainer}>
            <Box className={styles.pageSizeSelector}>
              <Typography variant="body2" component="span" style={{ marginRight: 8 }}>
                Rows per page:
              </Typography>
              <Select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                size="small"
                variant="outlined"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={15}>15</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </Box>
            {totalPages > 1 && (
              <PaginationComponent
                totalPages={totalPages}
                activePage={currentPage}
                setActivePage={setCurrentPage}
              />
            )}
          </Box>
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDownloadSingle}>
          <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
          Download QR
        </MenuItem>
        {selectedQR && !selectedQR.status && !selectedQR.is_used && (
          <MenuItem onClick={handleActivate}>Activate</MenuItem>
        )}
        {selectedQR && selectedQR.status && !selectedQR.is_used && (
          <MenuItem onClick={handleDeactivate}>Deactivate</MenuItem>
        )}
        {selectedQR && !selectedQR.is_used && (
          <MenuItem onClick={handleDeleteConfirm} style={{ color: "#f44336" }}>
            Delete
          </MenuItem>
        )}
      </Menu>

      <GenerateQRModal
        open={openGenerateModal}
        onClose={() => setOpenGenerateModal(false)}
        onSuccess={() => {
          setOpenGenerateModal(false);
          fetchQRCodes();
        }}
      />

      <GenerateBulkQRModal
        open={openBulkModal}
        onClose={() => setOpenBulkModal(false)}
        onSuccess={() => {
          setOpenBulkModal(false);
          fetchQRCodes();
          fetchEventNames();
        }}
      />

      <Confirmation
        open={openConfirm}
        handleClose={() => setOpenConfirm(false)}
        text="Are you sure you want to delete this QR code?"
        handleClick={handleDelete}
      />
    </Box>
  );
}

export default QRManagement;
